import React, { createContext, useMemo } from 'react';

import { ContextModel, DomainMetaModel, IntegrationEventAndPayloadModel, SchemaSummaryModel } from '../../../api/api';
import { ViewLoader } from '../../FlowDesigner/Components/ViewLoader';
import { useApiContexts } from '../Hooks/useApiContexts';
import useApiEntities from '../Hooks/useApiEntities';
import useApiIntegrationEvents from '../Hooks/useApiIntegrationEvents';

export interface IContextDomainMetadata {
	contextId: string;
	entities: SchemaSummaryModel[];
	entityById: Map<string, SchemaSummaryModel>;
	integrationEvents: IntegrationEventAndPayloadModel[];
	integrationEventById: Map<string, IntegrationEventAndPayloadModel>;
	contexts: ContextModel[];
	domainMeta: DomainMetaModel | null;
}

class ContextDomainMetadataImpl implements IContextDomainMetadata {
	contextId: string;
	entities: SchemaSummaryModel[];
	entityById: Map<string, SchemaSummaryModel>;
	integrationEvents: IntegrationEventAndPayloadModel[];
	integrationEventById: Map<string, IntegrationEventAndPayloadModel>;
	contexts: ContextModel[];
	domainMeta: DomainMetaModel | null;

	constructor(
		contextId: string,
		entities: SchemaSummaryModel[],
		integrationEvents: IntegrationEventAndPayloadModel[],
		contexts: ContextModel[],
		domainMeta: DomainMetaModel | null,
	) {
		this.contextId = contextId;
		this.entities = entities;
		this.integrationEvents = integrationEvents;
		this.domainMeta = domainMeta;
		this.contexts = contexts;

		this.entityById = entities.reduce((acc, entity) => {
			acc.set(entity.id, entity);
			return acc;
		}, new Map<string, SchemaSummaryModel>());

		this.integrationEventById = integrationEvents.reduce((acc, event) => {
			acc.set(event.event.id, event);
			return acc;
		}, new Map<string, IntegrationEventAndPayloadModel>());
	}
}

const emptySchemas: IContextDomainMetadata = {
	contextId: '',
	entities: [],
	entityById: new Map<string, SchemaSummaryModel>(),
	integrationEvents: [],
	integrationEventById: new Map<string, IntegrationEventAndPayloadModel>(),
	contexts: [],
	domainMeta: {} as DomainMetaModel,
};

export const ContextDomainMetadataContext = createContext<IContextDomainMetadata>(emptySchemas);

export default function ContextDomainMetadataProvider({ children, contextId }: React.PropsWithChildren<{ contextId: string }>) {
	const [schemasContext, setSchemasContext] = React.useState<IContextDomainMetadata>(emptySchemas);

	const { data: schemasMetadata, isLoading: schemasLoading } = useApiEntities(contextId);
	const { data: contextsData, isLoading: contextsLoading } = useApiContexts();
	const { data: integrationEventsData, isLoading: integrationEventsLoading } = useApiIntegrationEvents(contextId);

	const isLoaded = useMemo(
		() => !schemasLoading && !integrationEventsLoading && !contextsLoading && schemasMetadata && integrationEventsData && contextsData,
		[schemasLoading, integrationEventsLoading, contextsLoading, schemasMetadata, integrationEventsData, contextsData],
	);

	React.useEffect(() => {
		if (schemasMetadata && integrationEventsData && contextsData) {
			setSchemasContext(new ContextDomainMetadataImpl(contextId, schemasMetadata, integrationEventsData, contextsData, null));
		}
	}, [contextId, schemasMetadata, integrationEventsData, contextsData]);

	return (
		<ContextDomainMetadataContext.Provider value={schemasContext}>
			{!isLoaded && <ViewLoader />}
			{isLoaded && children}
		</ContextDomainMetadataContext.Provider>
	);
}

export function useContextDomainMetadata(): IContextDomainMetadata {
	const context = React.useContext(ContextDomainMetadataContext);
	if (!context) {
		throw new Error('useContextDomainMetadata must be used within a ContextDomainMetadataProvider');
	}
	return context;
}
